import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Truck, Zap, BadgeCheck, Search } from "lucide-react";
import { api } from "../lib/api";
import { usePageTitle } from "../lib/usePageTitle";

const FAQ = [
  { q: "Comment je paye mes 5,99€ ?", a: "Zéro paiement en ligne : tu coches l'option « Club STUFF » au moment de ta commande, et tu règles les 5,99€ directement au vendeur avec ta commande. Ton mois démarre dès la livraison." },
  { q: "C'est valable chez tous les vendeurs ?", a: "Oui ! Ton adhésion est liée à ton numéro de téléphone et fonctionne chez tous les vendeurs STUFF de France, y compris la boutique nationale (colis 48h)." },
  { q: "Il y a un engagement ?", a: "Aucun. Ton mois se termine, tu renouvelles si tu veux (recoche l'option à ta prochaine commande). Tu peux arrêter quand tu veux, sans frais." },
  { q: "La livraison est gratuite sur toutes mes commandes ?", a: "Sur toutes tes commandes d'au moins 69€, en illimité pendant 30 jours, chez tous les vendeurs STUFF. En dessous de 69€, les frais habituels du vendeur s'appliquent." },
];

export default function ClubStuff() {
  usePageTitle(
    "Club STUFF — Livraison gratuite illimitée dès 69€ | 5,99€/mois",
    "Rejoins le Club STUFF : livraison gratuite illimitée sur toutes tes commandes CBD dès 69€, pour 5,99€/mois seulement. Sans engagement, payable au vendeur."
  );
  const [phone, setPhone] = useState("");
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(false);

  const check = async () => {
    if (!phone.trim()) return;
    setChecking(true);
    try {
      const { data } = await api.get(`/club/status/${encodeURIComponent(phone.trim())}`);
      setStatus(data);
    } catch { setStatus(null); }
    finally { setChecking(false); }
  };

  return (
    <div data-testid="club-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-zinc-800">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(192,38,211,0.25),transparent_60%),radial-gradient(ellipse_at_bottom_right,rgba(157,255,0,0.12),transparent_55%)]" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center">
          <Crown className="h-10 w-10 text-neon-green mx-auto mb-3" />
          <p className="font-graffiti text-neon-magenta text-3xl mb-2">Le bon plan ultime</p>
          <h1 className="font-anton text-5xl sm:text-7xl uppercase text-white leading-none mb-4">
            CLUB <span className="neon-text-green">STUFF</span>
          </h1>
          <p className="text-zinc-300 text-lg max-w-2xl mx-auto mb-6">
            Livraison <span className="text-neon-green font-bold">gratuite en illimité</span> sur toutes tes commandes dès 69€ —
            pour seulement <span className="font-anton text-2xl neon-text-magenta">5,99€/mois</span>. Sans engagement.
          </p>
          <Link to="/" data-testid="club-order-cta" className="inline-block bg-neon-green text-black font-anton uppercase text-lg px-10 py-4 glow-green hover:bg-neon-magenta hover:text-white transition-colors">
            Je commande et je rejoins le Club
          </Link>
          <p className="text-zinc-500 text-xs mt-3">Coche simplement l'option « Club STUFF » dans ton panier 👌</p>
        </div>
      </section>

      {/* Avantages */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-ink-surface border border-neon-green/40 p-6 text-center">
            <Truck className="h-8 w-8 text-neon-green mx-auto mb-3" />
            <h3 className="font-anton text-lg uppercase text-white">Livraison offerte illimitée</h3>
            <p className="text-zinc-500 text-sm mt-1">Dès 69€ de commande, chez tous les vendeurs STUFF, autant de fois que tu veux.</p>
          </div>
          <div className="bg-ink-surface border border-neon-magenta/40 p-6 text-center">
            <Zap className="h-8 w-8 text-neon-magenta mx-auto mb-3" />
            <h3 className="font-anton text-lg uppercase text-white">Zéro paiement en ligne</h3>
            <p className="text-zinc-500 text-sm mt-1">Tu règles les 5,99€ au vendeur avec ta commande. Ton mois démarre à la livraison.</p>
          </div>
          <div className="bg-ink-surface border border-yellow-400/40 p-6 text-center">
            <BadgeCheck className="h-8 w-8 text-yellow-400 mx-auto mb-3" />
            <h3 className="font-anton text-lg uppercase text-white">Sans engagement</h3>
            <p className="text-zinc-500 text-sm mt-1">30 jours, tu renouvelles si tu veux. Tu arrêtes quand tu veux, sans frais.</p>
          </div>
        </div>

        {/* Rentabilité */}
        <div className="mt-8 border-2 border-neon-magenta bg-ink-surface p-6 text-center">
          <p className="text-zinc-300">Frais de livraison classiques : <span className="text-white font-bold">~3€ par commande</span>.
            Tu commandes <span className="text-neon-green font-bold">2 fois ou plus par mois</span> ? Le Club est déjà rentable. 🧠</p>
        </div>
      </section>

      {/* Comment ça marche */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-12">
        <h2 className="font-anton text-3xl uppercase text-white text-center mb-8">Comment ça <span className="neon-text-magenta">marche</span></h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[["1", "Coche l'option au panier", "Au moment de commander, coche « Rejoindre le Club STUFF (+5,99€) »."],
            ["2", "Paye au vendeur", "Tu règles ta commande + 5,99€ à la livraison. Rien en ligne, comme d'hab."],
            ["3", "Profite 30 jours", "Dès la livraison, ton Club est actif : livraison offerte dès 69€, en illimité."]].map(([n, title, text]) => (
            <div key={n} className="bg-ink-surface border border-zinc-800 p-6">
              <div className="font-anton text-5xl neon-text-green mb-2">{n}</div>
              <h3 className="font-anton text-lg uppercase text-white">{title}</h3>
              <p className="text-zinc-500 text-sm mt-1">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Vérifier mon statut */}
      <section className="max-w-xl mx-auto px-4 sm:px-6 pb-12">
        <div className="bg-ink-surface border border-zinc-800 p-6">
          <h2 className="font-anton text-xl uppercase text-white mb-3">Déjà membre ? Vérifie ton statut</h2>
          <div className="flex gap-2">
            <input data-testid="club-check-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Ton numéro de téléphone" className="flex-1 bg-ink border border-zinc-700 text-white px-4 py-3 focus:outline-none focus:border-neon-green" />
            <button data-testid="club-check-btn" onClick={check} disabled={checking || !phone.trim()} className="bg-neon-green text-black font-anton uppercase px-5 disabled:opacity-40"><Search className="h-5 w-5" /></button>
          </div>
          {status && (
            <div data-testid="club-check-result" className={`mt-4 px-4 py-3 border text-sm ${status.active ? "border-neon-green/50 bg-neon-green/5 text-neon-green" : "border-zinc-700 text-zinc-400"}`}>
              {status.active
                ? <>✅ Membre actif — livraison offerte dès 69€ jusqu'au <b>{new Date(status.paid_until).toLocaleDateString("fr-FR")}</b></>
                : status.status === "pending"
                  ? <>⏳ Adhésion en attente — elle s'activera à la livraison de ta commande.</>
                  : <>Pas encore membre. Coche l'option Club STUFF à ta prochaine commande !</>}
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-16">
        <h2 className="font-anton text-2xl uppercase text-white mb-4">Questions fréquentes</h2>
        <div className="space-y-3">
          {FAQ.map((f) => (
            <details key={f.q} className="bg-ink-surface border border-zinc-800 p-5 group">
              <summary className="font-semibold text-white cursor-pointer list-none flex justify-between items-center">{f.q}<span className="text-neon-green group-open:rotate-45 transition-transform text-xl">+</span></summary>
              <p className="text-zinc-400 text-sm mt-3">{f.a}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}
